import { useCallback, useEffect, useState } from 'react';
import { Box, Typography, Button, TextField, IconButton, Select, MenuItem, FormControl, InputLabel, Dialog, DialogTitle, DialogContent, DialogActions, useMediaQuery, Tabs, Tab } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { getPricing, createPricing, updatePricing, deletePricing } from '../../api/resourceApi';
import { getLogsByModules } from '../../api/logApi';
import { handlePriceWithRM } from '../../utils/priceUtils';
import { useUser } from '../../context/userContext';
import PriceHistoryTab from './priceHistoryTab';

const shortRM = (val) => {
    if (val == null || val === 0) return '-';
    return `RM${Math.round(val).toLocaleString('en-MY')}`;
};

// CC is derived from cash (cash ÷ factor, rounded UP to RM 10). Author cash only;
// the 12/24/36/60-month CC prices are computed. Latest rates per user 2026-06-17.
const CC_FACTORS = { 12: 0.955, 24: 0.935, 36: 0.925, 60: 0.9 };
const ccFromCash = (cash, term) => {
    const c = Number(cash) || 0;
    if (c <= 0) return 0;
    return Math.ceil(c / (CC_FACTORS[term] || 0.925) / 10) * 10;
};
const ccLine = (cash) => `12M ${shortRM(ccFromCash(cash, 12))} · 24M ${shortRM(ccFromCash(cash, 24))} · 36M ${shortRM(ccFromCash(cash, 36))} · 60M ${shortRM(ccFromCash(cash, 60))}`;

const PP = "'Poppins', sans-serif";

// Fixed list types - only these can be created
const LIST_TYPES = [
    { value: 'Residential Pricing', label: 'ATAP Pricing' },
    { value: 'Battery Pricing', label: 'Battery Pricing' },
];

export default function PricingTab({ isAdmin: isAdminProp } = {}) {
    const { user } = useUser();
    const isAdmin = isAdminProp ?? user?.roles?.some(r => ['Admin', 'Super Admin', 'Accountant'].includes(r));
    const [data, setData] = useState([]);
    const [tabIndex, setTabIndex] = useState(0);
    const [filterList, setFilterList] = useState('');
    const [editItem, setEditItem] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [lastUpdate, setLastUpdate] = useState(null);
    const [noteEdits, setNoteEdits] = useState({});
    const isMobile = useMediaQuery('(max-width:768px)');

    const fetchData = useCallback(async () => {
        try { const res = await getPricing(); setData(res.data || []); } catch (e) { console.error(e); }
    }, []);

    const fetchLastUpdate = useCallback(async () => {
        try {
            const res = await getLogsByModules('Pricing', 1, 1);
            if (res.data?.data?.length > 0) setLastUpdate(res.data.data[0]);
        } catch (e) { /* ignore */ }
    }, []);

    useEffect(() => { fetchData(); fetchLastUpdate(); }, [fetchData, fetchLastUpdate]);

    const listNames = [...new Set(data.map(d => d.listName))];
    const filtered = filterList ? data.filter(d => d.listName === filterList) : data;
    const grouped = {};
    filtered.forEach(d => {
        if (!grouped[d.listName]) grouped[d.listName] = { note: d.note, items: [] };
        grouped[d.listName].items.push(d);
    });

    const isBatteryList = (name) => name === 'Battery Pricing';

    const handleSave = async (addAnother = false) => {
        if (!editItem) return;
        // Author cash only — derive + persist the CC columns from cash (÷ factor,
        // round up RM10) so any surface still reading the columns stays consistent.
        const item = {
            ...editItem,
            cC36Price: ccFromCash(editItem.cashPrice, 36),
            cC60Price: ccFromCash(editItem.cashPrice, 60),
            cC36_3p8k: editItem.cash3p8k > 0 ? ccFromCash(editItem.cash3p8k, 36) : editItem.cC36_3p8k,
            cC60_3p8k: editItem.cash3p8k > 0 ? ccFromCash(editItem.cash3p8k, 60) : editItem.cC60_3p8k,
        };
        try {
            if (item.pricingId) {
                await updatePricing(item);
                setDialogOpen(false);
                setEditItem(null);
            } else {
                await createPricing(item);
                if (addAnother) {
                    if (isBatteryList(editItem.listName)) {
                        setEditItem(prev => ({ ...prev, pricingId: undefined, energy: 0, cashPrice: 0, cC36Price: 0, cC60Price: 0, note: '' }));
                    } else {
                        setEditItem(prev => ({ ...prev, pricingId: undefined, panelQty: 0, energy: 0, cashPrice: 0, cC36Price: 0, cC60Price: 0, cash3p8k: null, cC36_3p8k: null, cC60_3p8k: null }));
                    }
                } else {
                    setDialogOpen(false);
                    setEditItem(null);
                }
            }
            fetchData(); fetchLastUpdate();
        } catch (e) { alert('Failed to save'); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this pricing row?')) return;
        await deletePricing(id);
        fetchData(); fetchLastUpdate();
    };

    const openAdd = (listName) => {
        if (isBatteryList(listName)) {
            setEditItem({ listName, note: '', panelQty: 0, energy: 0, inverterSize: '', cashPrice: 0, cC36Price: 0, cC60Price: 0, sortOrder: 0 });
        } else {
            setEditItem({ listName, note: '', panelQty: 0, energy: 0, inverterSize: '', cashPrice: 0, cC36Price: 0, cC60Price: 0, cash3p8k: null, cC36_3p8k: null, cC60_3p8k: null, inverterSize3p: '', sortOrder: 0 });
        }
        setDialogOpen(true);
    };

    const openEdit = (item) => {
        setEditItem({ ...item });
        setDialogOpen(true);
    };

    const f = (field) => (e) => setEditItem(prev => ({ ...prev, [field]: e.target.value }));
    const fn = (field) => (e) => setEditItem(prev => ({ ...prev, [field]: parseFloat(e.target.value) || 0 }));

    const isBatteryEdit = editItem && isBatteryList(editItem.listName);

    return (
        <Box>
            {/* Tabs visible to everyone — Price History is view-only for non-admins.
                Editing in either tab stays gated by isAdmin (Admin/Super Admin/Accountant). */}
            <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)} sx={{ mb: 2, borderBottom: '1px solid #E2E8F0', '& .MuiTab-root': { fontFamily: PP, textTransform: 'none', fontSize: '0.85rem', fontWeight: 600 } }}>
                <Tab label="Live Pricing" />
                <Tab label="Price History" />
            </Tabs>
            {tabIndex === 1 ? (
                <PriceHistoryTab canEdit={isAdmin} />
            ) : (
                <Box>
            {lastUpdate && (
                <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8', fontFamily: PP, mb: 1 }}>
                    Last updated: {new Date(lastUpdate.createdOn).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' })} by {lastUpdate.createdBy} — {lastUpdate.action}: {lastUpdate.remarks}
                </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Filter by list</InputLabel>
                    <Select value={filterList} onChange={e => setFilterList(e.target.value)} label="Filter by list">
                        <MenuItem value="">All</MenuItem>
                        {listNames.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                    </Select>
                </FormControl>
                {isAdmin && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {LIST_TYPES.map(lt => (
                            <Button key={lt.value} size="small" startIcon={<AddIcon />} variant="outlined" onClick={() => openAdd(lt.value)}
                                sx={{ fontFamily: PP, fontWeight: 600, textTransform: 'none', borderRadius: '8px', fontSize: '0.75rem' }}>
                                + {lt.label}
                            </Button>
                        ))}
                    </Box>
                )}
            </Box>

            {Object.entries(grouped).map(([listName, group]) => (
                <Box key={listName} sx={{ mb: 3 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', fontFamily: PP, mb: 0.5 }}>{listName}</Typography>
                    {isAdmin ? (
                        <Box sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'flex-start' }}>
                            <TextField
                                value={noteEdits[listName] ?? group.note ?? ''}
                                onChange={e => setNoteEdits(prev => ({ ...prev, [listName]: e.target.value }))}
                                placeholder="Add note for this list..."
                                size="small" fullWidth multiline
                                sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.78rem', fontFamily: PP, color: '#64748B', borderRadius: '6px' } }}
                            />
                            <Button size="small" variant="contained"
                                onClick={async () => {
                                    const newNote = noteEdits[listName];
                                    if (newNote == null) return;
                                    const items = data.filter(d => d.listName === listName);
                                    try {
                                        for (const item of items) {
                                            await updatePricing({ ...item, note: newNote });
                                        }
                                        // Reload data BEFORE clearing the edit, so the TextField
                                        // never briefly falls back to the stale group.note.
                                        await fetchData();
                                        fetchLastUpdate();
                                        setNoteEdits(prev => { const n = { ...prev }; delete n[listName]; return n; });
                                    } catch (err) {
                                        console.error('Save note failed:', err);
                                        alert('Failed to save note. Check console for details.');
                                    }
                                }}
                                sx={{ fontFamily: PP, fontWeight: 600, textTransform: 'none', borderRadius: '6px', fontSize: '0.72rem', whiteSpace: 'nowrap', minWidth: 'auto', px: 2, background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)', boxShadow: 'none' }}>
                                Save Note
                            </Button>
                        </Box>
                    ) : (
                        group.note && <Typography sx={{ fontSize: '0.78rem', color: '#64748B', fontFamily: PP, mb: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{group.note}</Typography>
                    )}

                    {isBatteryList(listName) ? (
                        isMobile ? (
                        /* Battery: Mobile cards */
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {group.items.map(item => (
                                <Box key={item.pricingId} sx={{ p: 1.5, borderRadius: '8px', border: '1px solid #E2E8F0', backgroundColor: '#FAFAFA' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.8 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', fontFamily: PP }}>🔋 {item.note || item.inverterSize || '-'}</Typography>
                                            <Typography sx={{ fontSize: '0.7rem', color: '#94A3B8', fontFamily: PP }}>{item.energy} kWh</Typography>
                                        </Box>
                                        {isAdmin && (
                                            <Box sx={{ display: 'flex', gap: 0.3 }}>
                                                <IconButton size="small" onClick={() => openEdit(item)}><EditIcon sx={{ fontSize: 13 }} /></IconButton>
                                                <IconButton size="small" onClick={() => handleDelete(item.pricingId)}><DeleteIcon sx={{ fontSize: 13, color: '#EF4444' }} /></IconButton>
                                            </Box>
                                        )}
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                                        <Box sx={{ flex: 1, textAlign: 'center' }}>
                                            <Typography sx={{ fontSize: '0.58rem', color: '#94A3B8', fontFamily: PP }}>Cash</Typography>
                                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#10B981', fontFamily: PP }}>{shortRM(item.cashPrice)}</Typography>
                                        </Box>
                                        <Box sx={{ flex: 1, textAlign: 'center' }}>
                                            <Typography sx={{ fontSize: '0.58rem', color: '#94A3B8', fontFamily: PP }}>36M CC</Typography>
                                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, fontFamily: PP }}>{shortRM(ccFromCash(item.cashPrice, 36))}</Typography>
                                        </Box>
                                        <Box sx={{ flex: 1, textAlign: 'center' }}>
                                            <Typography sx={{ fontSize: '0.58rem', color: '#94A3B8', fontFamily: PP }}>60M CC</Typography>
                                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, fontFamily: PP }}>{shortRM(ccFromCash(item.cashPrice, 60))}</Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                        ) : (
                        /* Battery: Desktop table */
                        <Box sx={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: PP, maxWidth: 600 }}>
                                <thead>
                                    <tr style={{ borderBottom: '2px solid #E2E8F0' }}>
                                        {['Battery Type', 'Capacity (kWh)', 'Cash Price', '36M CC Price', '60M CC Price'].map(h => (
                                            <th key={h} style={{ padding: '8px 10px', fontSize: '0.72rem', fontWeight: 600, color: '#64748B', textAlign: h === 'Battery Type' ? 'left' : 'right', textTransform: 'uppercase' }}>{h}</th>
                                        ))}
                                        {isAdmin && <th style={{ width: 60 }}></th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {group.items.map(item => (
                                        <tr key={item.pricingId} style={{ borderBottom: '1px solid #F1F5F9' }}>
                                            <td style={{ padding: '8px 10px', fontWeight: 600, fontSize: '0.82rem' }}>{item.note || item.inverterSize || '-'}</td>
                                            <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: '0.82rem', fontWeight: 600 }}>{item.energy} kWh</td>
                                            <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: '0.82rem', color: '#10B981', fontWeight: 600 }}>{shortRM(item.cashPrice)}</td>
                                            <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: '0.82rem' }}>{shortRM(ccFromCash(item.cashPrice, 36))}</td>
                                            <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: '0.82rem' }}>{shortRM(ccFromCash(item.cashPrice, 60))}</td>
                                            {isAdmin && (
                                                <td style={{ padding: '4px' }}>
                                                    <IconButton size="small" onClick={() => openEdit(item)}><EditIcon sx={{ fontSize: 14 }} /></IconButton>
                                                    <IconButton size="small" onClick={() => handleDelete(item.pricingId)}><DeleteIcon sx={{ fontSize: 14, color: '#EF4444' }} /></IconButton>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </Box>
                        )
                    ) : isMobile ? (
                        /* Mobile: Compact cards */
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {group.items.map(item => (
                                <Box key={item.pricingId} sx={{ p: 1.5, borderRadius: '8px', border: '1px solid #E2E8F0', backgroundColor: '#FAFAFA' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.8 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                            <Typography sx={{ fontWeight: 700, fontSize: '1rem', fontFamily: PP }}>{item.energy} kWp</Typography>
                                            <Typography sx={{ fontSize: '0.7rem', color: '#94A3B8', fontFamily: PP }}>{item.panelQty} panels</Typography>
                                        </Box>
                                        {isAdmin && (
                                            <Box sx={{ display: 'flex', gap: 0.3 }}>
                                                <IconButton size="small" onClick={() => openEdit(item)}><EditIcon sx={{ fontSize: 13 }} /></IconButton>
                                                <IconButton size="small" onClick={() => handleDelete(item.pricingId)}><DeleteIcon sx={{ fontSize: 13, color: '#EF4444' }} /></IconButton>
                                            </Box>
                                        )}
                                    </Box>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: '50px 1fr 1fr 1fr', gap: 0.3, alignItems: 'center' }}>
                                        <Box />
                                        <Typography sx={{ fontSize: '0.58rem', color: '#94A3B8', fontFamily: PP, textAlign: 'center' }}>Cash</Typography>
                                        <Typography sx={{ fontSize: '0.58rem', color: '#94A3B8', fontFamily: PP, textAlign: 'center' }}>36M CC</Typography>
                                        <Typography sx={{ fontSize: '0.58rem', color: '#94A3B8', fontFamily: PP, textAlign: 'center' }}>60M CC</Typography>
                                        <Typography sx={{ fontSize: '0.65rem', color: '#64748B', fontFamily: PP, fontWeight: 600 }}>{item.inverterSize}</Typography>
                                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#10B981', fontFamily: PP, textAlign: 'center' }}>{shortRM(item.cashPrice)}</Typography>
                                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, fontFamily: PP, textAlign: 'center' }}>{shortRM(ccFromCash(item.cashPrice, 36))}</Typography>
                                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, fontFamily: PP, textAlign: 'center' }}>{shortRM(ccFromCash(item.cashPrice, 60))}</Typography>
                                        {item.cash3p8k != null && item.cash3p8k > 0 && (
                                            <>
                                                <Typography sx={{ fontSize: '0.62rem', color: '#94A3B8', fontFamily: PP, fontWeight: 600 }}>{item.inverterSize3p || '3p'}</Typography>
                                                <Typography sx={{ fontSize: '0.7rem', color: '#64748B', fontFamily: PP, textAlign: 'center' }}>{shortRM(item.cash3p8k)}</Typography>
                                                <Typography sx={{ fontSize: '0.7rem', color: '#64748B', fontFamily: PP, textAlign: 'center' }}>{shortRM(ccFromCash(item.cash3p8k, 36))}</Typography>
                                                <Typography sx={{ fontSize: '0.7rem', color: '#64748B', fontFamily: PP, textAlign: 'center' }}>{shortRM(ccFromCash(item.cash3p8k, 60))}</Typography>
                                            </>
                                        )}
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    ) : (
                        /* Desktop: Table */
                        <Box sx={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: PP }}>
                                <thead>
                                    <tr style={{ borderBottom: '2px solid #E2E8F0' }}>
                                        {['Panels', 'kWp', 'Inverter', 'Cash', '36M CC', '60M CC', '3P Inverter', '3P Cash', '3P 36M', '3P 60M'].map(h => (
                                            <th key={h} style={{ padding: '8px 10px', fontSize: '0.72rem', fontWeight: 600, color: '#64748B', textAlign: h === 'Panels' ? 'left' : 'right', textTransform: 'uppercase' }}>{h}</th>
                                        ))}
                                        {isAdmin && <th style={{ width: 60 }}></th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {group.items.map(item => (
                                        <tr key={item.pricingId} style={{ borderBottom: '1px solid #F1F5F9' }}>
                                            <td style={{ padding: '8px 10px', fontWeight: 600, fontSize: '0.82rem' }}>{item.panelQty}</td>
                                            <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: '0.82rem' }}>{item.energy}</td>
                                            <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: '0.82rem' }}>{item.inverterSize}</td>
                                            <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: '0.82rem', color: '#10B981', fontWeight: 600 }}>{shortRM(item.cashPrice)}</td>
                                            <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: '0.82rem' }}>{shortRM(ccFromCash(item.cashPrice, 36))}</td>
                                            <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: '0.82rem' }}>{shortRM(ccFromCash(item.cashPrice, 60))}</td>
                                            <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: '0.78rem', color: '#64748B' }}>{item.inverterSize3p || '-'}</td>
                                            <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: '0.78rem', color: '#64748B' }}>{shortRM(item.cash3p8k)}</td>
                                            <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: '0.78rem', color: '#64748B' }}>{shortRM(ccFromCash(item.cash3p8k, 36))}</td>
                                            <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: '0.78rem', color: '#64748B' }}>{shortRM(ccFromCash(item.cash3p8k, 60))}</td>
                                            {isAdmin && (
                                                <td style={{ padding: '4px' }}>
                                                    <IconButton size="small" onClick={() => openEdit(item)}><EditIcon sx={{ fontSize: 14 }} /></IconButton>
                                                    <IconButton size="small" onClick={() => handleDelete(item.pricingId)}><DeleteIcon sx={{ fontSize: 14, color: '#EF4444' }} /></IconButton>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </Box>
                    )}
                </Box>
            ))}

            {filtered.length === 0 && <Typography sx={{ textAlign: 'center', color: '#94A3B8', fontFamily: PP, py: 4 }}>No pricing data yet</Typography>}

            {/* Edit/Add Dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontFamily: PP, fontWeight: 700 }}>
                    {editItem?.pricingId ? 'Edit' : 'Add'} — {editItem?.listName}
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
                    {isBatteryEdit ? (
                        /* Battery form */
                        <>
                            <TextField label="Battery Name / Type" value={editItem?.note || ''} onChange={f('note')} size="small" placeholder="e.g. 14.3kWh GoodWe" />
                            <TextField label="Capacity (kWh)" type="number" value={editItem?.energy || ''} onChange={fn('energy')} size="small" />
                            <TextField label="Cash Price (RM)" type="number" value={editItem?.cashPrice ?? ''} onChange={fn('cashPrice')} size="small" fullWidth />
                            <Typography sx={{ fontSize: '0.7rem', color: '#64748B', fontFamily: PP }}>CC (auto): {ccLine(editItem?.cashPrice)}</Typography>
                        </>
                    ) : (
                        /* Panel pricing form */
                        <>
                            <Box sx={{ display: 'flex', gap: 1.5 }}>
                                <TextField label="Panels" type="number" value={editItem?.panelQty || ''} onChange={fn('panelQty')} size="small" fullWidth />
                                <TextField label="kWp" type="number" value={editItem?.energy || ''} onChange={fn('energy')} size="small" fullWidth />
                                <TextField label="Inverter" value={editItem?.inverterSize || ''} onChange={f('inverterSize')} size="small" fullWidth placeholder="e.g. 1p5k" />
                            </Box>
                            <Typography sx={{ fontSize: '0.75rem', color: '#1A8CFF', fontFamily: PP, fontWeight: 600 }}>Single Phase</Typography>
                            <TextField label="Cash Price" type="number" value={editItem?.cashPrice ?? ''} onChange={fn('cashPrice')} size="small" fullWidth />
                            <Typography sx={{ fontSize: '0.7rem', color: '#64748B', fontFamily: PP }}>CC (auto): {ccLine(editItem?.cashPrice)}</Typography>
                            <Typography sx={{ fontSize: '0.75rem', color: '#8B5CF6', fontFamily: PP, fontWeight: 600 }}>3-Phase</Typography>
                            <Box sx={{ display: 'flex', gap: 1.5 }}>
                                <TextField label="3P Inverter" value={editItem?.inverterSize3p || ''} onChange={f('inverterSize3p')} size="small" fullWidth placeholder="e.g. 3p8k" />
                                <TextField label="3P Cash" type="number" value={editItem?.cash3p8k ?? ''} onChange={fn('cash3p8k')} size="small" fullWidth />
                            </Box>
                            {editItem?.cash3p8k > 0 && <Typography sx={{ fontSize: '0.7rem', color: '#64748B', fontFamily: PP }}>3P CC (auto): {ccLine(editItem?.cash3p8k)}</Typography>}
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setDialogOpen(false)} sx={{ fontFamily: PP, textTransform: 'none' }}>Cancel</Button>
                    {!editItem?.pricingId && (
                        <Button onClick={() => handleSave(true)} variant="outlined" sx={{ fontFamily: PP, fontWeight: 600, textTransform: 'none', borderRadius: '8px', borderColor: '#1A8CFF', color: '#1A8CFF' }}>Save & Add Another</Button>
                    )}
                    <Button onClick={() => handleSave(false)} variant="contained" sx={{ background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)', fontFamily: PP, fontWeight: 600, textTransform: 'none', borderRadius: '8px', boxShadow: 'none' }}>Save</Button>
                </DialogActions>
            </Dialog>
                </Box>
            )}
        </Box>
    );
}
